import { QRCodeSVG } from "qrcode.react";
import { createPortal } from "react-dom";

import { BottomSheet } from "../BottomSheet";
import { useBodyScrollLock, useIsMobile } from "../hooks";
import { DownloadIcon } from "../Icons/index";
import { CrossIcon } from "../Icons/index";
import { Typo } from "../Typo";

export type QRCodeModalTranslations = {
  title: string;
  description: string;
  downloadButton: string;
};

type QRCodeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  translations: QRCodeModalTranslations;
  onDownload?: () => void;
  isMobile?: boolean;
};

export const QRCodeModal = ({
  isOpen,
  onClose,
  url,
  translations,
  onDownload,
  isMobile: isMobileProp,
}: QRCodeModalProps) => {
  const isMobileHook = useIsMobile();
  const isMobile = isMobileProp ?? isMobileHook;
  // Mobile renders a BottomSheet, which locks scroll itself.
  useBodyScrollLock(isOpen && !isMobile);

  const handleDownload = () => {
    onDownload?.();
    const svgEl = document.getElementById(
      "qr-code-svg",
    ) as SVGSVGElement | null;
    if (!svgEl) return;

    const size = svgEl.width.baseVal.value || 200;
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const svgBlob = new Blob([svgData], {
      type: "image/svg+xml;charset=utf-8",
    });
    const svgUrl = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);
      URL.revokeObjectURL(svgUrl);

      const link = document.createElement("a");
      link.download = "referral-qr-code.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    };
    img.src = svgUrl;
  };

  const downloadButton = (
    <button
      type="button"
      onClick={handleDownload}
      data-testid="qr-code-download-button"
      className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-surface-brand-default px-3 py-2 hover:opacity-90 lg:w-fit lg:min-w-[188px]"
    >
      <DownloadIcon />
      <Typo element="span" type="labelSM" textColor="text-text-inverse-primary">
        {translations.downloadButton}
      </Typo>
    </button>
  );

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        title={translations.title}
        footer={downloadButton}
        isDraggable={true}
      >
        <div data-testid="qr-code-modal" className="-mt-4 flex flex-col gap-4">
          <Typo type="bodySM" textColor="text-text-secondary">
            {translations.description}
          </Typo>
          <div className="flex justify-center rounded-lg bg-surface-secondary p-10">
            <QRCodeSVG
              id="qr-code-svg"
              data-testid="qr-code-svg"
              value={url}
              size={200}
              level="M"
              marginSize={4}
              title={`QR code for ${url}`}
            />
          </div>
        </div>
      </BottomSheet>
    );
  }

  if (!isOpen) return null;

  return createPortal(
    <div
      data-testid="qr-code-modal"
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div
        className="absolute inset-0 animate-modal-fade-in bg-black/60"
        onClick={onClose}
        data-testid="qr-code-modal-overlay"
      />
      <div className="relative z-10 mx-4 w-full max-w-lg animate-modal-scale-in rounded-2xl bg-white p-6 shadow-xl lg:mx-0">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col">
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              data-testid="qr-code-modal-close"
              className="flex self-end"
            >
              <CrossIcon className="h-6 w-6" />
            </button>
            <div className="flex items-start justify-between">
              <div>
                <Typo type="headingXL">{translations.title}</Typo>
                <Typo
                  type="bodySM"
                  textColor="text-text-secondary"
                  className="mt-2"
                >
                  {translations.description}
                </Typo>
              </div>
            </div>
          </div>

          <div className="flex justify-center rounded-lg bg-surface-secondary p-10">
            <QRCodeSVG
              id="qr-code-svg"
              data-testid="qr-code-svg"
              value={url}
              size={200}
              level="M"
              marginSize={4}
              title={`QR code for ${url}`}
            />
          </div>

          <div className="flex justify-center">{downloadButton}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
